import React, { useState } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { authService } from '../../services';

const ChangeTPinModal = ({ show, handleClose }) => {
  const [formData, setFormData] = useState({
    oldTPin: '',
    newTPin: '',
    confirmTPin: ''
  });
  
  const [showTPins, setShowTPins] = useState({
    old: false,
    new: false,
    confirm: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Handle input change - only allow digits
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Only allow digits and limit to 4 or 6 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Toggle T-PIN visibility
  const toggleTPinVisibility = (field) => {
    setShowTPins(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Old T-PIN validation
    if (!formData.oldTPin) {
      newErrors.oldTPin = 'Current T-PIN is required';
    } else if (formData.oldTPin.length < 4) {
      newErrors.oldTPin = 'T-PIN must be at least 4 digits';
    }

    // New T-PIN validation
    if (!formData.newTPin) {
      newErrors.newTPin = 'New T-PIN is required';
    } else if (formData.newTPin.length < 4) {
      newErrors.newTPin = 'T-PIN must be at least 4 digits';
    } else if (formData.newTPin === formData.oldTPin) {
      newErrors.newTPin = 'New T-PIN must be different from current T-PIN';
    }

    // Confirm T-PIN validation
    if (!formData.confirmTPin) {
      newErrors.confirmTPin = 'Please confirm your T-PIN';
    } else if (formData.confirmTPin !== formData.newTPin) {
      newErrors.confirmTPin = 'T-PINs do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrors({});

    try {
      await authService.changeTPin(formData.oldTPin, formData.newTPin);
      
      setSuccessMessage('T-PIN changed successfully!');
      
      // Reset form
      setFormData({
        oldTPin: '',
        newTPin: '',
        confirmTPin: ''
      });
      
      // Close modal after 1.5 seconds
      setTimeout(() => {
        handleClose();
        setSuccessMessage('');
      }, 1500);
      
    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to change T-PIN. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const onHide = () => {
    setFormData({
      oldTPin: '',
      newTPin: '',
      confirmTPin: ''
    });
    setErrors({});
    setSuccessMessage('');
    setShowTPins({
      old: false,
      new: false,
      confirm: false
    });
    handleClose();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-shield-lock-fill me-2"></i>
          Change Transaction PIN
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {successMessage && (
          <div className="alert alert-success d-flex align-items-center" role="alert">
            <i className="bi bi-check-circle-fill me-2"></i>
            {successMessage}
          </div>
        )}
        
        {errors.submit && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {errors.submit}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Current T-PIN */}
          <Form.Group className="mb-3">
            <Form.Label>Current T-PIN <span className="text-danger">*</span></Form.Label>
            <InputGroup>
              <Form.Control
                type={showTPins.old ? "text" : "password"}
                name="oldTPin"
                placeholder="Enter current T-PIN"
                value={formData.oldTPin}
                onChange={handleChange}
                isInvalid={!!errors.oldTPin}
                disabled={isSubmitting}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <Button 
                variant="outline-secondary" 
                onClick={() => toggleTPinVisibility('old')}
                disabled={isSubmitting}
              >
                <i className={`bi bi-eye${showTPins.old ? '-slash' : ''}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.oldTPin}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          {/* New T-PIN */}
          <Form.Group className="mb-3">
            <Form.Label>New T-PIN <span className="text-danger">*</span></Form.Label>
            <InputGroup>
              <Form.Control
                type={showTPins.new ? "text" : "password"}
                name="newTPin"
                placeholder="Enter new T-PIN"
                value={formData.newTPin}
                onChange={handleChange}
                isInvalid={!!errors.newTPin}
                disabled={isSubmitting}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <Button 
                variant="outline-secondary" 
                onClick={() => toggleTPinVisibility('new')}
                disabled={isSubmitting}
              >
                <i className={`bi bi-eye${showTPins.new ? '-slash' : ''}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.newTPin}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              T-PIN must be 4-6 digits long
            </Form.Text>
          </Form.Group>

          {/* Confirm T-PIN */}
          <Form.Group className="mb-3">
            <Form.Label>Confirm New T-PIN <span className="text-danger">*</span></Form.Label>
            <InputGroup>
              <Form.Control
                type={showTPins.confirm ? "text" : "password"}
                name="confirmTPin"
                placeholder="Re-enter new T-PIN"
                value={formData.confirmTPin}
                onChange={handleChange}
                isInvalid={!!errors.confirmTPin}
                disabled={isSubmitting}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <Button 
                variant="outline-secondary" 
                onClick={() => toggleTPinVisibility('confirm')}
                disabled={isSubmitting}
              >
                <i className={`bi bi-eye${showTPins.confirm ? '-slash' : ''}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.confirmTPin}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Changing...
            </>
          ) : (
            'Change T-PIN'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeTPinModal;

